import React from 'react';
import Toast from '@components/Toast';
import { commonImage } from '@images/imageUrls';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { readLinkWhitelist } from '@server';


interface setPostProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    setPost?: any;
    topic?: any;
}


import type { BasePageState } from '../../types/base';

interface SetPostState extends BasePageState {
    topicList: any[];
    categoryList: string[];
    activeCategory: string;
    categoryMap?: { [name: string]: string };
    categoryBadgeMap?: { [name: string]: number };
    topicsCategoryCache?: { [categoryName: string]: any[] };
    isInitialized?: boolean;
    whiteList: string[];
    whiteHttpList: string[];
    hasMore?: boolean;
    isLoadingMore?: boolean;
}

// 清理HTTP域名白名单，去掉前缀点和转换格式
const cleanWhiteHttpList = (whiteHttpList: string[]) => {
    const cleaned = whiteHttpList
        .filter(domain => typeof domain === 'string')
        .map(domain => domain.startsWith('.') ? domain.slice(1) : domain);
    return cleaned;
};

@inject('app')
@inject('setPost')
@inject('topic')
@inject('site')
@inject('user')
@observer

class SetPost extends BasePage<setPostProps, SetPostState> {
    state: SetPostState = {
        loading: false,
        error: null,
        pageLoaded: false,
        topicList: [],
        categoryList: [],
        activeCategory: '',
        categoryMap: {},
        categoryBadgeMap: {},
        topicsCategoryCache: {},
        isInitialized: false,
        whiteList: [],
        whiteHttpList: [],
        hasMore: true,
        isLoadingMore: false,
    };

    async componentDidMount() {
        super.componentDidMount();

        // 登录检查：未登录则由 user.checkLogin 处理跳转，停止后续初始化
        const { user, app, site } = this.props as any;
        const platform = app?.platform || 'h5';
        if (user && typeof user.checkLogin === 'function') {
            const isLogin = user.checkLogin(platform);
            if (!isLogin) {
                console.log('[SetPost] User not logged in, aborting init (checkLogin handled redirect)');
                return;
            }
        }

        // 获取权限信息（如果还未加载）
        if (site && !site.siteConfig) {
            await site.fetchSiteConfig();
        }

        // 等待话题ID处理完成（先获取话题详情填充缓存，避免显示占位符）
        await this.handleTopicIdFromUrl();
        if (this.state.isInitialized) {
            await this.loadEditPost();
        } else {

            await Promise.allSettled([
                this.initTopicCategoryAndList(),
                this.loadEditPost(),
            ]);
        }
        // 获取链接白名单
        this.onGetUrlWhitelist();
    }

    componentDidUpdate(prevProps: any, prevState: any) {
        // 监听话题缓存变化，实时更新分类徽章
        const currentTopicCache = this.props.setPost?.topicCache || {};
        const prevTopicCache = prevProps?.setPost?.topicCache || {};
        const currentTopicIds = this.props.setPost?.postData?.topicIds || [];

        // 检查话题缓存是否有更新（特别是分类信息的更新）
        if (currentTopicIds.length > 0) {
            const cacheChanged = currentTopicIds.some(topicId => {
                const current = currentTopicCache[topicId];
                const prev = prevTopicCache[topicId];
                return current?.category !== prev?.category;
            });

            if (cacheChanged) {
                console.log('[SetPost] Topic cache categories updated, refreshing badges');
                this.updateCategoryBadgesFromCache();
            }
        }

        if (this.state.isInitialized &&
            this.state.topicList.length === 0 &&
            prevState.topicList.length !== 0) {
            console.warn('[SetPost] componentDidUpdate - topicList became empty! Previous length:', prevState.topicList.length);

            if (this.state.activeCategory) {
                const cached = this.state.topicsCategoryCache?.[this.state.activeCategory];
                if (cached && cached.length > 0) {
                    console.log('[SetPost] componentDidUpdate restoring topicList from cache (became empty):', this.state.activeCategory);
                    this.setState({ topicList: cached });
                    return;
                }
            }
        }


        if (this.state.isInitialized &&
            this.state.topicList.length === 0 &&
            prevState.topicList.length === 0) {
            const cacheKeys = Object.keys(this.state.topicsCategoryCache || {});
            if (cacheKeys.length > 0) {
                const firstAvailableCategory = cacheKeys[0];
                const cached = this.state.topicsCategoryCache[firstAvailableCategory];
                if (cached && cached.length > 0) {
                    console.log('[SetPost] componentDidUpdate force restoring from cache:', firstAvailableCategory);
                    this.setState({
                        topicList: cached,
                        activeCategory: firstAvailableCategory
                    });
                    return;
                }
            }
        }


        if (this.state.isInitialized && this.state.topicList.length === 0) {
            const cacheKeys = Object.keys(this.state.topicsCategoryCache || {});
            if (cacheKeys.length > 0 && prevState.topicList.length === 0) {

                const firstAvailableCategory = cacheKeys[0];
                const cachedTopics = this.state.topicsCategoryCache[firstAvailableCategory];
                if (cachedTopics && cachedTopics.length > 0) {
                    console.log('[SetPost] componentDidUpdate restoring from first available cache:', firstAvailableCategory);
                    this.setState({
                        topicList: cachedTopics,
                        activeCategory: firstAvailableCategory
                    });
                }
            }
        }
    }

    componentWillUnmount() {
        // 页面离开时清空发帖数据，避免数据污染
        const { setPost } = this.props;
        if (setPost && setPost.clearPostData) {
            console.log('[SetPost] componentWillUnmount - 清空发帖数据');
            setPost.clearPostData();
        }
    }

    handleTopicIdFromUrl = async () => {
        const params = new URLSearchParams(this.props.location.search);
        const topicIdParam = params.get('topicId');
        if (!topicIdParam) return;

        const topicIds = topicIdParam.split(',').map(id => id.trim()).filter(id => id);
        if (topicIds.length > 0) {
            const { setPost, topic } = this.props;
            if (topic && topic.readTopicsByIds) {
                try {
                    const topicIdStr = topicIds.join(',');
                    const response = await topic.readTopicsByIds({ filter: { id: topicIdStr } });
                    const topicsData = response?.data?.data || response?.Data?.data || [];
                    if (setPost && topicsData.length > 0) {
                        const topicCache = setPost.topicCache || {};
                        topicsData.forEach((t: any) => {
                            topicCache[String(t.id)] = {
                                id: String(t.id),
                                name: t.name,
                                cover: t.avatar || commonImage.official2,
                                count: `${t.threadCount || 0}帖子`,
                                category: t.category?.name,
                                isHot: t.isHot,
                                isNew: t.isNew,
                                isRecommended: t.isRecommended,
                            };
                        });
                        setPost.topicCache = topicCache;
                    }
                } catch (error) {
                    console.warn('[SetPost] handleTopicIdFromUrl - failed to fetch topic details:', error);
                }
            }
            
            // 设置话题ID
            setPost?.setPostData && setPost.setPostData({ topicIds });
        }
    }

    loadEditPost = async () => {
        const params = new URLSearchParams(this.props.location.search);
        const id = params.get('id');
        if (!id) return;
        const { setPost } = this.props;
        setPost?.setPostData && setPost.setPostData({ id });
        if (!setPost || !setPost.fetchPostDetail) {
            return;
        }
        try {
            const detail = await setPost.fetchPostDetail(id);
            if(detail?.type == 1) { // 如果是微帖，就不可在web端编辑，跳转到首页
                this.props.history && this.props.history.push(`/`);
            }
            if (detail) {
                setPost.loadPostDetail(detail);

                // 编辑帖子时，如果帖子包含话题信息但当前话题列表为空，需要特殊处理
                const postTopicIds = setPost.postData?.topicIds || [];
                if (postTopicIds.length > 0) {
                    // 尝试通过topic store获取话题详情以补充分类信息
                    const { topic } = this.props;
                    if (topic && setPost.fetchTopicDetailsViaStore) {
                        setTimeout(() => {
                            setPost.fetchTopicDetailsViaStore(postTopicIds, topic);
                        }, 200);
                    }

                    // 延迟处理话题分类信息，等待话题列表和分类加载完成
                    setTimeout(() => {
                        this.updateTopicCategoriesForEdit(postTopicIds);
                    }, 500);
                }
            } else {
            }
        } catch (error) {
            console.error('[SetPost] loadEditPost error:', error);
        }
    }

    initTopicCategoryAndList = async () => {
        const { topic } = this.props;
        if (!topic || !topic.getTopicCategoryList) return;

        try {

            const res = await topic.getTopicCategoryList({ 'page[number]': 1, 'page[size]': 20 });
            const categoryArr = res?.data?.data || [];
            if (!categoryArr.length) return;

            const categoryList = categoryArr.map((c: any) => c.name);
            const categoryMap = categoryArr.reduce((acc: any, c: any) => { acc[c.name] = c.id; return acc; }, {});
            const activeCategory = categoryArr[0].name;
            const activeCategoryId = categoryArr[0].id;


            const selectedTopicIds = this.props.setPost?.postData?.topicIds || [];
            const categoryBadgeMap: { [key: string]: number } = {};


            const tasks: any[] = [
                topic.getTopicList({ 'filter[categoryId]': activeCategoryId, 'page[number]': 1, 'page[size]': 20 })
            ];

            if (selectedTopicIds.length > 0) {
                const topicIdStr = selectedTopicIds.join(',');
                tasks.push(topic.readTopicsByIds({ filter: { id: topicIdStr } }));
            }

            const results = await Promise.allSettled(tasks);


            let firstCategoryTopics: any[] = [];
            if (results[0].status === 'fulfilled') {
                // 直接从返回值获取当前页数据，避免 listStore 累积问题
                const response = results[0].value as any;
                const resData = response?.data || response?.Data;
                const rawList = resData?.data || [];
                firstCategoryTopics = rawList.map((item: any) => ({
                    id: String(item.id),
                    name: item.name,
                    cover: item.avatar || commonImage.official2,
                    count: `${item.threadCount || 0}帖子 ${item.discussionCount || 0}讨论量`,
                    category: activeCategory,
                    isHot: item.isHot,
                    isNew: item.isNew,
                    isRecommended: item.isRecommended,
                }));


                const { setPost } = this.props;
                if (setPost) {
                    const topicCache = setPost.topicCache || {};
                    rawList.forEach((item: any) => {
                        topicCache[String(item.id)] = {
                            id: String(item.id),
                            name: item.name,
                            cover: item.avatar || commonImage.official2,
                            count: `${item.threadCount || 0}帖子 ${item.discussionCount || 0}讨论量`,
                            category: activeCategory,
                            isHot: item.isHot,
                            isNew: item.isNew,
                            isRecommended: item.isRecommended,
                        };
                    });
                    setPost.topicCache = topicCache;
                }
            }


            if (selectedTopicIds.length > 0 && results[1] && results[1].status === 'fulfilled') {
                const response = results[1].value as any;
                const selectedTopics = response?.data?.data || response?.Data?.data || [];


                const { setPost } = this.props;
                if (setPost && selectedTopics.length > 0) {
                    const topicCache = setPost.topicCache || {};
                    selectedTopics.forEach((t: any) => {
                        topicCache[String(t.id)] = {
                            id: String(t.id),
                            name: t.name,
                            cover: t.avatar || commonImage.official2,
                            count: `${t.threadCount || 0}帖子`,
                            category: t.category?.name,
                            isHot: t.isHot,
                            isNew: t.isNew,
                            isRecommended: t.isRecommended,
                        };
                    });
                    setPost.topicCache = topicCache;
                }


                selectedTopics.forEach((t: any) => {
                    const categoryName = t.category?.name;
                    if (categoryName) {
                        categoryBadgeMap[categoryName] = (categoryBadgeMap[categoryName] || 0) + 1;
                    }
                });
            }


            const topicsCategoryCache = { [activeCategory]: firstCategoryTopics };

            // 根据初始加载的数量判断是否还有更多（每页20条）
            const hasMore = firstCategoryTopics.length >= 20;

            this.setState({
                topicList: firstCategoryTopics,
                categoryList,
                activeCategory,
                categoryMap,
                categoryBadgeMap: categoryBadgeMap,
                topicsCategoryCache,
                isInitialized: true,
                hasMore,
                isLoadingMore: false,
            });
        } catch (error) {
            console.error('[SetPost] initTopicCategoryAndList error:', error);
        }
    }

    updateTopicCategoriesForEdit = (postTopicIds: string[]) => {
        const { setPost } = this.props;
        if (!setPost || !this.state.isInitialized) return;

        const topicCache = setPost.topicCache || {};
        const categoryBadgeMap = { ...this.state.categoryBadgeMap };
        let hasUpdates = false;

        // 遍历帖子中的话题ID，尝试为没有分类信息的话题补充分类
        postTopicIds.forEach(topicId => {
            const cachedTopic = topicCache[topicId];
            if (cachedTopic && !cachedTopic.category) {
                // 在所有分类的缓存中查找这个话题
                const topicsCategoryCache = this.state.topicsCategoryCache || {};
                for (const [categoryName, topicList] of Object.entries(topicsCategoryCache)) {
                    const found = topicList.find((t: any) => String(t.id) === topicId);
                    if (found) {
                        // 更新话题缓存中的分类信息
                        cachedTopic.category = categoryName;
                        hasUpdates = true;
                        break;
                    }
                }
            }

            // 计算分类徽章数量
            if (cachedTopic && cachedTopic.category) {
                categoryBadgeMap[cachedTopic.category] = (categoryBadgeMap[cachedTopic.category] || 0) + 1;
            }
        });

        if (hasUpdates) {
            setPost.topicCache = topicCache;
            console.log('[SetPost] Updated topic categories for edit mode');
        }

        this.setState({ categoryBadgeMap });
    }

    // 监听话题缓存变化，实时更新分类徽章
    updateCategoryBadgesFromCache = () => {
        const { setPost } = this.props;
        if (!setPost) return;

        const topicCache = setPost.topicCache || {};
        const postTopicIds = setPost.postData?.topicIds || [];
        const categoryBadgeMap = {};

        // 根据话题缓存重新计算分类徽章
        postTopicIds.forEach(topicId => {
            const cachedTopic = topicCache[topicId];
            if (cachedTopic && cachedTopic.category) {
                categoryBadgeMap[cachedTopic.category] = (categoryBadgeMap[cachedTopic.category] || 0) + 1;
            }
        });

        console.log('[SetPost] Updated category badges from cache:', categoryBadgeMap);
        this.setState({ categoryBadgeMap });
    }

    loadTopicsForCategory = async (categoryName: string, page: number = 1) => {
        const { topic, setPost } = this.props;
        console.log(topic);
        if (!topic?.getTopicList) return;

        try {
            const categoryId = this.state.categoryMap?.[categoryName];
            if (!categoryId) return;

            // 注意：第一页时的清空已在 handleCategoryChange 中处理

            // 分页加载：每页加载20条
            const res = await topic.getTopicList({ 'filter[categoryId]': categoryId, 'page[number]': page, 'page[size]': 20 });
            // 直接从返回值获取当前页数据，避免 listStore 累积问题
            const resData = res?.data || res?.Data;
            const rawList = resData?.data || [];
            const pageInfo = resData?.page || {};
            const hasMore = pageInfo.hasMore !== false && rawList.length >= 20;

            if (setPost && rawList.length > 0) {
                const topicCache = setPost.topicCache || {};
                rawList.forEach((item: any) => {
                    topicCache[String(item.id)] = {
                        id: String(item.id),
                        name: item.name,
                        cover: item.avatar || commonImage.official2,
                        count: `${item.threadCount || 0}帖子 ${item.discussionCount || 0}讨论量`,
                        category: categoryName,
                        isHot: item.isHot,
                        isNew: item.isNew,
                        isRecommended: item.isRecommended,
                    };
                });
                setPost.topicCache = topicCache;
            }

            const categoryTopics = rawList.map((item: any) => ({
                id: String(item.id),
                name: item.name,
                cover: item.avatar || commonImage.official2,
                count: `${item.threadCount || 0}帖子 ${item.discussionCount || 0}讨论量`,
                category: categoryName,
                isHot: item.isHot,
                isNew: item.isNew,
                isRecommended: item.isRecommended,
            }));

            // 分页加载时，需要追加而不是替换
            if (page === 1) {
                // 第一页：替换
                const topicsCategoryCache = { ...this.state.topicsCategoryCache, [categoryName]: categoryTopics };
                this.setState({ topicList: categoryTopics, topicsCategoryCache, hasMore, isLoadingMore: false });
            } else {
                // 后续页：追加
                const prevList = this.state.topicList || [];
                // 仅保留当前分类的数据，避免其他分类的遗留混入
                const currentList = prevList.filter(item => item.category === categoryName);
                const newList = [...currentList, ...categoryTopics];
                const topicsCategoryCache = { ...this.state.topicsCategoryCache, [categoryName]: newList };
                this.setState({ topicList: newList, topicsCategoryCache, hasMore, isLoadingMore: false });
            }

            // 在编辑模式下，检查是否需要更新已选话题的分类信息
            const postTopicIds = setPost?.postData?.topicIds || [];
            if (postTopicIds.length > 0) {
                const topicCache = setPost.topicCache || {};
                let hasUpdates = false;

                categoryTopics.forEach(categoryTopic => {
                    if (postTopicIds.includes(String(categoryTopic.id))) {
                        const cachedTopic = topicCache[String(categoryTopic.id)];
                        if (cachedTopic && !cachedTopic.category) {
                            cachedTopic.category = categoryName;
                            hasUpdates = true;
                            console.log(`[SetPost] Updated category for topic ${categoryTopic.name} to ${categoryName}`);
                        }
                    }
                });

                if (hasUpdates) { 
                    setPost.topicCache = topicCache;
                }
            }
        } catch (error) {
            console.error('[SetPost] loadTopicsForCategory error:', error);
        }
    }

    handleCategoryChange = async (categoryName: string, page: number = 1) => {
        console.log('[SetPost] handleCategoryChange:', categoryName, 'page:', page);

        // 如果是第一页且有缓存，直接使用缓存
        if (page === 1) {
            const cached = this.state.topicsCategoryCache?.[categoryName];
            if (cached && cached.length > 0) {
                console.log('[SetPost] Using cached topics for:', categoryName, 'count:', cached.length);
                // 使用缓存时，根据缓存大小判断是否还有更多（假设每页20条）
                const hasMore = cached.length >= 20;
                this.setState({ activeCategory: categoryName, topicList: cached, hasMore, isLoadingMore: false });
                return;
            }
            // 没有缓存时，先清空列表再请求
            this.setState({ activeCategory: categoryName, topicList: [] });
        }

        // 加载数据
        console.log('[SetPost] Loading from API:', categoryName, 'page:', page);
        this.setState({ isLoadingMore: page > 1 });
        await this.loadTopicsForCategory(categoryName, page);
    };
    setPostData = (data: any) => {
        const { setPost } = this.props
        setPost.setPostData(data);
    }
    onPublishPost = async (coverFile?: File | null, videoFile?: File | null) => {
        const { setPost } = this.props;
        this.setState({ loading: true });
        try {
            const isEdit = !!setPost?.postData?.id;
            const result = isEdit
                ? await setPost.updatePost(coverFile, videoFile)
                : await setPost.createPost(coverFile, videoFile);
            const successMsg = isEdit ? '编辑成功' : '发布成功';
            if (result.code === 0) {
                const threadId = result?.data?.id || setPost?.postData?.id;
                if (Toast) {
                    Toast.success(successMsg);
                    if (threadId) {
                        this.props.history.push('/threadDetail/' + threadId);
                    }
                } else {
                    alert(successMsg);
                }
            } else {
                const errMsg = result?.msg || result?.message || '操作失败';
                if (Toast) {
                    Toast.error(errMsg);
                } else {
                    alert(errMsg);
                }
            }
        } finally {
            this.setState({ loading: false });
        }
    }

    // 获取链接白名单
    onGetUrlWhitelist = async () => {
        console.log('[SetPost] onGetUrlWhitelist - 开始获取链接白名单');
        const res = await readLinkWhitelist() as any;
        console.log('[SetPost] onGetUrlWhitelist - API响应:', res);

        if (res?.code === 0) {
            const whiteList = res?.data?.hostScheme || [];
            const whiteHttpList = cleanWhiteHttpList(res?.data?.host?.concat(res?.data?.hostBrowser) || []);

            console.log('[SetPost] onGetUrlWhitelist - 处理后的白名单数据:', {
                whiteList,
                whiteHttpList,
                rawHostScheme: res?.data?.hostScheme,
                rawHost: res?.data?.host,
                rawHostBrowser: res?.data?.hostBrowser
            });

            this.setState({
                whiteList,
                whiteHttpList
            });

            console.log('[SetPost] onGetUrlWhitelist - 白名单设置完成');
        } else {
            console.error('[SetPost] onGetUrlWhitelist - 获取白名单失败:', res);
            Toast.error({ content: '获取链接白名单失败' });
        }
    }

    // 清理HTTP链接，支持白名单域名检查
    cleanHttpLinks = (html: string, whiteHttpList: string[]) => {
        // 预处理白名单，全部转小写、去掉前后空格
        const domains = whiteHttpList.map(domain => domain.replace(/^\.+/, '').trim().toLowerCase());
        return html.replace(/<a\s+[^>]*href="(https?:\/\/[^"]+)"[^>]*>(.*?)<\/a>/gi, (match, href, inner) => {
            try {
                const url = new URL(href);
                const host = url.host.toLowerCase();
                // 域名白名单支持主域名和二级域名
                const isWhite = domains.some(domain =>
                    host === domain ||
                    host.endsWith('.' + domain)
                );
                if (isWhite) {
                    return match; // 保留原a标签
                } else {
                    // 不在白名单，去掉整个a标签，只保留内容
                    return inner;
                }
            } catch {
                // 解析失败也去掉a标签
                return inner;
            }
        });
    }

    // 清理自定义链接（deeplink），支持白名单检查
    cleanCustomLinks = (html: string, whitelist: string[]) => {
        return html.replace(/<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, (match, href, inner) => {
            // 1. https://后面是deeplink（如vivo://等）
            const httpsDeeplink = href.match(/^https:\/\/([a-z0-9\.\-\+]+:\/\/.+)/i);
            if (httpsDeeplink) {
                const deeplink = httpsDeeplink[1];
                const isWhite = whitelist.some(scheme => deeplink.startsWith(scheme));
                if (isWhite) {
                    return match; // 白名单，保留原样
                } else {
                    // 非白名单deeplink，去掉整个a标签，只保留内容
                    return inner;
                }
            }

            // 2. 直接是deeplink协议（非http/https），判断是否白名单
            const isDeeplink = /^[a-z0-9\.\-\+]+:\/\//i.test(href) && !/^https?:\/\//i.test(href);
            if (isDeeplink) {
                const isWhite = whitelist.some(scheme => href.startsWith(scheme));
                if (isWhite) {
                    return match;
                } else {
                    return inner;
                }
            }

            // 3. 其它（普通http/https等）全部保留原样
            return match;
        });
    }

    render() {
        const { app } = this.props;
        const platform = app?.platform || 'h5';
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {

            let topicList = this.state.topicList;

            console.log('[SetPost] render debug:', {
                originalTopicListLength: this.state.topicList.length,
                isInitialized: this.state.isInitialized,
                activeCategory: this.state.activeCategory,
                cacheKeys: Object.keys(this.state.topicsCategoryCache || {}),
                categoryListLength: this.state.categoryList.length
            });


            if (topicList.length === 0 && this.state.isInitialized) {
                console.log('[SetPost] render - topicList is empty, trying to restore from cache');


                if (this.state.activeCategory) {
                    const cached = this.state.topicsCategoryCache?.[this.state.activeCategory];
                    if (cached && cached.length > 0) {
                        console.log('[SetPost] render - restored from activeCategory cache:', this.state.activeCategory, 'count:', cached.length);
                        topicList = cached;
                    }
                }


                if (topicList.length === 0) {
                    const cacheKeys = Object.keys(this.state.topicsCategoryCache || {});
                    if (cacheKeys.length > 0) {
                        const firstAvailableCategory = cacheKeys[0];
                        const cached = this.state.topicsCategoryCache[firstAvailableCategory];
                        if (cached && cached.length > 0) {
                            console.log('[SetPost] render - restored from first available cache:', firstAvailableCategory, 'count:', cached.length);
                            topicList = cached;
                        }
                    }
                }

                if (topicList.length === 0) {
                    console.warn('[SetPost] render - failed to restore topicList from cache!');
                }
            }

            return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (
                    <>
                        {console.log('[SetPost] render - 传递给PC组件的白名单:', {
                            whiteList: this.state.whiteList,
                            whiteHttpList: this.state.whiteHttpList
                        })}
                        <PC
                            {...this.props}
                            setPostData={this.setPostData}
                            onPublishPost={this.onPublishPost}
                            topicList={topicList}
                            categoryList={this.state.categoryList}
                            onCategoryChange={this.handleCategoryChange}
                            loading={this.state.loading}
                            whiteList={this.state.whiteList}
                            whiteHttpList={this.state.whiteHttpList}
                            hasMore={this.state.hasMore}
                            isLoadingMore={this.state.isLoadingMore}
                        />
                    </>
                ) : (
                    <H5
                        {...this.props}
                        setPostData={this.setPostData}
                        onPublishPost={this.onPublishPost}
                        topicList={topicList}
                        categoryList={this.state.categoryList}
                        onCategoryChange={this.handleCategoryChange}
                        loading={this.state.loading}
                        hasMore={this.state.hasMore}
                        isLoadingMore={this.state.isLoadingMore}
                    />
                )}
            </div>)
        }
    }
}

export default withRouter(SetPost);